import { createApi } from '@reduxjs/toolkit/query/react'
import { axiosBaseQuery } from '../axiosBaseQuery'
import { Table } from '../../models/table'
import { ManufacturerModel } from '../../models/manufacturer-model'

export enum SystemTableId {
  Manufacturers = 1,
  Warehouses    = 2,
  Tags          = 3,
  SortCodes     = 4,
  ItemGroups    = 5,
  Banks         = 6,
}


export const systemTablesApi = createApi({
  reducerPath: 'systemTablesApi',
  baseQuery: axiosBaseQuery({ baseUrl: '/api/' }),
  tagTypes: [
    'SystemTables',
    'SystemTablesOptions',
    'Models',
  ],
  endpoints: (b) => ({
    

    // ---------- GET ----------

    getSystemTables: b.query<Table[], void>({
      query: () => ({ url: 'system-tables' }),
      providesTags: ['SystemTables'],
      keepUnusedDataFor: Infinity,
    }),


    getSystemTableOptions: b.query<Table[], number>({
      query: (tableNumber) => ({ url: `system-tables-items/${tableNumber}` }),
      providesTags: (_res, _err, tableNumber) => [
        { type: 'SystemTablesOptions', id: tableNumber },
      ],
      keepUnusedDataFor: Infinity,
    }),


    getManufacturerModelsById: b.query<ManufacturerModel[], number>({
      query: (manufacturer_id) => ({
        url: `models/search/0/name/asc`,
        method: 'Post',
        data: { manufacturer_id },
      }),
      providesTags: (_res, _err, manufacturer_id) => [
        { type: 'Models', id: manufacturer_id },
      ],
      keepUnusedDataFor: Infinity,
    }),



    // ---------- PATCH ----------

    editSystemTable: b.mutation<Table, Table>({
      query: (systemTableToUpdate) => ({
        url: `system-tables/${systemTableToUpdate.id}`,
        method: 'PATCH',
        data: systemTableToUpdate,
      }),
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          systemTablesApi.util.updateQueryData('getSystemTables', undefined, draft => {
            const row = draft.find(t => t.id === arg.id)
            if (row) Object.assign(row, arg)
          })
        )
        try { await queryFulfilled } catch { patch.undo() }
      },
      invalidatesTags: (_res, _err, arg) => [
        { type: 'SystemTables' as const, id: arg.id },
        { type: 'SystemTables' as const, id: 'LIST' as const },
      ],
    }),


    editSystemTableOptionById: b.mutation<Table, { systemTableNumber: number; option: Table }>({
      query: ({ systemTableNumber, option }) => ({
        url: `system-tables-items/${systemTableNumber}/${option.id}`,
        method: 'PATCH',
        data: option,
      }),
      async onQueryStarted({ systemTableNumber, option }, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          systemTablesApi.util.updateQueryData('getSystemTableOptions', systemTableNumber, draft => {
            const row = draft.find(r => r.id === option.id)
            if (row) Object.assign(row, option)
          })
        )
        try { await queryFulfilled } catch { patch.undo() }
      },
      invalidatesTags: (_res, _err, {systemTableNumber}) => [
        { type: 'SystemTablesOptions', id: systemTableNumber },
      ],
    }),


    editModelOptionById: b.mutation<Table, ManufacturerModel>({
      query: (model) => ({
        url: `models/${model.id}`,
        method: 'PATCH',
        data: model,
      }),
      async onQueryStarted(model, { dispatch, queryFulfilled }) {
        const patch = dispatch(
          systemTablesApi.util.updateQueryData('getManufacturerModelsById', model.manufacturer_id, draft => {
            const row = draft.find(r => r.id === model.id)
            if (row) Object.assign(row, model)
          })
        )
        try { await queryFulfilled } catch { patch.undo() }
      },
      invalidatesTags: (_res, _err, {manufacturer_id}) => [
        { type: 'Models', id: manufacturer_id },
      ],
    }),

    // ---------- POST ----------

    addSystemTableOption: b.mutation<Table, { systemTableNumber: number; optionIndex: number }>({
      query: ({ systemTableNumber, optionIndex }) => ({
        url: `system-tables-items/${systemTableNumber}`,
        method: 'POST',
        data: { index: optionIndex },
      }),
      async onQueryStarted({ systemTableNumber, optionIndex }, { dispatch, queryFulfilled }) {
        const temp: Table = {id: 99999, business_id: 99999, index: optionIndex, name: ""}
        const patch = dispatch(
          systemTablesApi.util.updateQueryData('getSystemTableOptions', systemTableNumber, draft => {
            draft.push(temp)
          })
        )
        try {
          const { data: created } = await queryFulfilled
          // swap temp with real
          dispatch(
            systemTablesApi.util.updateQueryData('getSystemTableOptions', systemTableNumber, draft => {
              const i = draft.findIndex(x => x.id === temp.id)
              if (i >= 0) draft[i] = created
            })
          )
        } catch {
          patch.undo()
        }
      },
      invalidatesTags: (_res, _err, { systemTableNumber }) => [
        { type: 'SystemTablesOptions', id: systemTableNumber},
      ],
    }),



    addManufacturerModel: b.mutation<ManufacturerModel, { manufacturerId: number, modelIndex: number }>({
      query: ({ manufacturerId, modelIndex }) => ({
        url: `models`,
        method: 'POST',
        data: { 
          manufacturer_id: manufacturerId,
          index: modelIndex
        },
      }),
      async onQueryStarted({ manufacturerId, modelIndex }, { dispatch, queryFulfilled }) {
        const temp: ManufacturerModel = {id: 99999, business_id: 99999, manufacturer_id: manufacturerId, index: modelIndex, created_at: "", updated_at: "", name: ""}
        const patch = dispatch(
          systemTablesApi.util.updateQueryData('getManufacturerModelsById', manufacturerId, draft => {
            draft.push(temp)
          })
        )
        try {
          const { data: created } = await queryFulfilled
          // swap temp with real
          dispatch(
            systemTablesApi.util.updateQueryData('getManufacturerModelsById', manufacturerId, draft => {
              const i = draft.findIndex(x => x.id === temp.id)
              if (i >= 0) draft[i] = created
            })
          )
        } catch {
          patch.undo()
        }
      },
      invalidatesTags: (_res, _err, { manufacturerId }) => [
        { type: 'Models', id: manufacturerId},
      ],
    }),

  }),
})

export const {
  useGetSystemTablesQuery,
  useGetSystemTableOptionsQuery,

  useGetManufacturerModelsByIdQuery,
  useLazyGetManufacturerModelsByIdQuery,

  useEditSystemTableMutation,
  useEditSystemTableOptionByIdMutation,
  useEditModelOptionByIdMutation,

  useAddSystemTableOptionMutation,
  useAddManufacturerModelMutation

} = systemTablesApi



/**
 * get options for table by table number
 * @param tableNumber
 * 1.manufacturers
 * 2.Warehouses
 * 3.tags
 * 4.sortCodes
 * 5.ItemGroups
 * 6.Banks
 * @returns Table[]
 */
export const useLazyGetTableOptionsByTableIndexQuery = () =>
  systemTablesApi.useLazyGetSystemTableOptionsQuery();


/**
 * @returns Manufacturers table options
 */
export const useGetManufacturersTableQuery = () =>
  systemTablesApi.useGetSystemTableOptionsQuery(SystemTableId.Manufacturers);

/**
 * @returns Warehouses table options
 */
export const useGetWarehousesTableQuery = () =>
  systemTablesApi.useGetSystemTableOptionsQuery(SystemTableId.Warehouses);

/**
 * @returns Tags table options
 */
export const useGetTagsTableQuery = () =>
  systemTablesApi.useGetSystemTableOptionsQuery(SystemTableId.Tags);

/**
 * @returns SortCodes table options
 */
export const useGetSortCodesTableQuery = () =>
  systemTablesApi.useGetSystemTableOptionsQuery(SystemTableId.SortCodes);

/**
 * @returns ItemGroups table options
 */
export const useGetItemGroupsTableQuery = () =>
  systemTablesApi.useGetSystemTableOptionsQuery(SystemTableId.ItemGroups);

/**
 * @returns Banks table options
 */
export const useGetBanksTableQuery = () =>
  systemTablesApi.useGetSystemTableOptionsQuery(SystemTableId.Banks);

/**
 * @returns models for manufacturer by id
 * @param manufacturerId 
 */
export const useGetModelsByManufacturerIdQuery = (manufacturerId: number) =>
  systemTablesApi.useGetManufacturerModelsByIdQuery(manufacturerId);